import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  Users,
  FileText,
  DollarSign,
  Globe,
  Edit3,
  Save,
  MessageSquare,
  CheckSquare,
  AlertTriangle,
  Clock,
  UserPlus,
  X,
  Trash2,
  CheckCircle2,
  Plus,
  CreditCard,
  ShieldCheck,
  ReceiptText,
  ExternalLink,
} from "lucide-react";
import { clientsApi, mattersApi, usersApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import { formatPhoneNumber } from "../../utils/formatPhone";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import ComposeMessageModal from "./ComposeMessageModal";
import ConflictCheckBanner from "../conflicts/ConflictCheckBanner";
import ClientMessagesTab from "./ClientMessagesTab";
import ClientDocumentsSection from "./ClientDocumentsSection";
import { useMatterInspector } from "../../contexts/MatterInspectorContext";
import { useAssistantContext } from "../assistant/assistantContextStore";
import { notifyError } from "../ui/Toast";

type Tab = "overview" | "matters" | "messages" | "documents" | "billing" | "portal";

const tabs: { id: Tab; label: string; Icon: React.FC<any> }[] = [
  { id: "overview", label: "Overview", Icon: Users },
  { id: "matters", label: "Matters", Icon: Briefcase },
  { id: "messages", label: "Messages", Icon: MessageSquare },
  { id: "documents", label: "Documents", Icon: FileText },
  { id: "billing", label: "Billing", Icon: DollarSign },
  { id: "portal", label: "Portal", Icon: Globe },
];


const statusBadge: Record<string, string> = {
  open: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
  on_hold: "bg-amber-100 text-amber-700",
};

// ── Communication Preferences Card ──────────────────────────────

const COMM_PREF_FIELDS = [
  { key: "invoiceNotifications", label: "Invoice Notifications" },
  { key: "paymentReceipts", label: "Payment Receipts" },
  { key: "appointmentReminders", label: "Appointment Reminders" },
  { key: "matterUpdates", label: "Matter Updates" },
  { key: "documentRequests", label: "Document Requests" },
  { key: "preferredChannel", label: "Preferred Channel" },
  { key: "generalCommunications", label: "General Communications" },
] as const;

const NOTIF_OPTIONS = [
  { value: "", label: "Use firm default" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "both", label: "Both" },
  { value: "none", label: "None" },
];

const CHANNEL_OPTIONS = [
  { value: "", label: "Use firm default" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "phone", label: "Phone" },
  { value: "whatsapp", label: "WhatsApp" },
];

const GENERAL_OPTIONS = [
  { value: "", label: "Use firm default" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

function getOptionsForKey(key: string) {
  if (key === "preferredChannel") return CHANNEL_OPTIONS;
  if (key === "generalCommunications") return GENERAL_OPTIONS;
  return NOTIF_OPTIONS;
}

function getDisplayValue(key: string, rawValue: any): { label: string; isDefault: boolean } {
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return { label: "Firm default", isDefault: true };
  }
  if (key === "generalCommunications") {
    return { label: rawValue ? "Yes" : "No", isDefault: false };
  }
  const formatted = String(rawValue).charAt(0).toUpperCase() + String(rawValue).slice(1);
  return { label: formatted, isDefault: false };
}

function getBadgeClasses(key: string, rawValue: any): string {
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return "border border-dashed border-slate-300 text-slate-400 bg-white";
  }
  if (key === "generalCommunications") {
    return rawValue
      ? "bg-emerald-100 text-emerald-700"
      : "bg-slate-100 text-slate-500";
  }
  const val = String(rawValue);
  switch (val) {
    case "email":
      return "bg-blue-100 text-blue-700";
    case "sms":
      return "bg-purple-100 text-purple-700";
    case "both":
      return "bg-emerald-100 text-emerald-700";
    case "none":
      return "bg-slate-100 text-slate-500 line-through";
    case "phone":
      return "bg-amber-100 text-amber-700";
    case "whatsapp":
      return "bg-green-100 text-green-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
}

interface CommPrefsCardProps {
  client: any;
  editing: boolean;
  form: Record<string, string>;
  saving: boolean;
  onStartEdit: () => void;
  onCancel: () => void;
  onChange: (form: Record<string, string>) => void;
  onSave: () => void;
}

const CommunicationPreferencesCard: React.FC<CommPrefsCardProps> = ({
  client,
  editing,
  form,
  saving,
  onStartEdit,
  onCancel,
  onChange,
  onSave,
}) => {
  const prefs = client.communicationPreferences || {};

  const updateField = (key: string, value: string) => {
    const next = { ...form };
    if (value === "") {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 lg:col-span-2">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
          Communication Preferences
        </h3>
        {!editing ? (
          <button
            onClick={onStartEdit}
            className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
            style={{ color: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-primary)")}
          >
            <Edit3 className="h-3.5 w-3.5" />
            Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-bold transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              <Save className="h-3 w-3" />
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        /* View mode */
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {COMM_PREF_FIELDS.map((field) => {
            const rawValue = prefs[field.key];
            const { label, isDefault } = getDisplayValue(field.key, rawValue);
            const badgeClasses = getBadgeClasses(field.key, rawValue);

            return (
              <div key={field.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">{field.label}</span>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badgeClasses}`}>
                    {label}
                  </span>
                  {isDefault && (
                    <span className="text-[10px] text-slate-400">(firm default)</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Edit mode */
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {COMM_PREF_FIELDS.map((field) => {
            const options = getOptionsForKey(field.key);
            return (
              <div key={field.key} className="p-3 bg-slate-50 rounded-lg">
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  {field.label}
                </label>
                <SearchableSelect
                  options={options.map((opt) => ({ value: opt.value, label: opt.label }))}
                  value={form[field.key] || ""}
                  onChange={(val) => updateField(field.key, val || "")}
                  placeholder="Use firm default"
                  clearable
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openMatterInspector } = useMatterInspector();
  const { userData, firmData } = useUserDataContext();
  const [client, setClient] = useState<any>(null);
  const [matters, setMatters] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [firmUsers, setFirmUsers] = useState<any[]>([]);
  const [addingTeamMember, setAddingTeamMember] = useState(false);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Communication preferences state
  const [editingCommPrefs, setEditingCommPrefs] = useState(false);
  const [commPrefsForm, setCommPrefsForm] = useState<Record<string, string>>({});
  const [savingCommPrefs, setSavingCommPrefs] = useState(false);
  const isAdmin = userData?._id === firmData?.adminUserId;
  useAssistantContext(client?._id ? { type: "client", id: client._id, name: client.displayName } : null);

  const fetchClient = useCallback(async () => {
    if (!id) return;
    try {
      const res = await clientsApi.get(id);
      setClient(res.data);
      setEditForm({
        displayName: res.data.displayName || "",
        email: res.data.emails?.[0]?.email || "",
        phone: res.data.phones?.[0]?.number || "",
      });
    } catch (err) {
      console.error("Failed to fetch client:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchMatters = useCallback(async () => {
    if (!id) return;
    try {
      const res = await mattersApi.list({ clientId: id });
      setMatters(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch matters:", err);
    }
  }, [id]);

  const fetchSummary = useCallback(async () => {
    if (!id) return;
    setLoadingSummary(true);
    try {
      const res = await clientsApi.getSummary(id);
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch client summary:", err);
    } finally {
      setLoadingSummary(false);
    }
  }, [id]);

  const fetchFirmUsers = useCallback(async () => {
    try {
      const res = await usersApi.list();
      setFirmUsers(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch firm users:", err);
    }
  }, []);

  useEffect(() => {
    fetchClient();
    fetchMatters();
    fetchSummary();
    fetchFirmUsers();
  }, [fetchClient, fetchMatters, fetchSummary, fetchFirmUsers]);

  const handleDelete = async () => {
    if (!id || !isAdmin) return;
    setDeleting(true);
    try {
      await clientsApi.remove(id);
      navigate("/clients");
    } catch (err) {
      console.error("Failed to delete client:", err);
      notifyError("Couldn't delete the client.", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;
    try {
      await clientsApi.update(id, {
        displayName: editForm.displayName,
        emails: editForm.email ? [{ label: "main", email: editForm.email }] : [],
        phones: editForm.phone ? [{ label: "main", number: editForm.phone }] : [],
      });
      setEditing(false);
      fetchClient();
    } catch (err) {
      console.error("Failed to update client:", err);
      notifyError("Couldn't save the client.", err);
    }
  };

  const handleAddTeamMember = async (userId: string) => {
    if (!id || !client) return;
    const currentIds = (client.assignedUserIds || []).map((u: any) => u._id || u);
    try {
      await clientsApi.assign(id, { assignedUserIds: [...currentIds, userId] });
      setAddingTeamMember(false);
      fetchClient();
    } catch (err) {
      console.error("Failed to add team member:", err);
      notifyError("Couldn't add the team member.", err);
    }
  };

  const handleRemoveTeamMember = async (userId: string) => {
    if (!id || !client) return;
    const currentIds = (client.assignedUserIds || []).map((u: any) => u._id || u);
    try {
      await clientsApi.assign(id, { assignedUserIds: currentIds.filter((uid: string) => uid !== userId) });
      fetchClient();
    } catch (err) {
      console.error("Failed to remove team member:", err);
      notifyError("Couldn't remove the team member.", err);
    }
  };

  const formatPracticeArea = (pa: string) =>
    pa?.split("_").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Client not found</p>
        <button
          onClick={() => navigate("/clients")}
          className="mt-4 text-brand font-medium text-sm hover:underline"
        >
          Back to Clients
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Back button + Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/clients")}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Clients
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div
              className="h-14 w-14 rounded-full flex items-center justify-center text-white font-bold text-lg"
              style={{ backgroundColor: "var(--color-accent)" }}
            >
              {client.displayName
                .split(" ")
                .slice(0, 2)
                .map((w: string) => w[0]?.toUpperCase())
                .join("")}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{client.displayName}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {client.type === "organization" ? (
                    <Building2 className="h-3 w-3" />
                  ) : (
                    <Users className="h-3 w-3" />
                  )}
                  {client.type?.charAt(0).toUpperCase() + client.type?.slice(1)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowComposeModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              <MessageSquare className="h-4 w-4" />
              Send Message
            </button>
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <Edit3 className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <div className="flex gap-1">
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            const Icon = tab.Icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  active ? "text-brand" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {active && (
                  <motion.div
                    layoutId="clientTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-accent"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
        >
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Conflict check banner */}
              <ConflictCheckBanner
                clientId={id}
                clientName={client.displayName}
                clientEmail={client.emails?.[0]?.email}
                clientPhone={client.phones?.[0]?.number}
              />

              {/* Key metrics row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Tasks Card */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <CheckSquare className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                    Tasks
                  </h3>
                  {summary ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-3xl font-bold text-slate-800">{summary.tasks.openCount}</p>
                          <p className="text-sm text-slate-500">Open tasks</p>
                        </div>
                        {summary.tasks.overdueCount > 0 && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-full">
                            <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                            <span className="text-sm font-medium text-red-700">{summary.tasks.overdueCount} overdue</span>
                          </div>
                        )}
                      </div>
                      {summary.tasks.upcoming.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-slate-100">
                          <p className="text-xs text-slate-400 uppercase font-semibold">Upcoming</p>
                          {summary.tasks.upcoming.map((task: any) => (
                            <div key={task._id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-700 truncate">{task.title}</span>
                              <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                                {new Date(task.dueAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="animate-pulse space-y-3">
                      <div className="h-10 bg-slate-100 rounded-lg" />
                      <div className="h-6 bg-slate-100 rounded-lg w-2/3" />
                    </div>
                  )}
                </div>

                {/* Billing Card */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                    Billing
                  </h3>
                  {summary ? (
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Outstanding</p>
                        <p className="text-2xl font-bold text-slate-800">
                          ${(summary.billing.outstandingBalanceCents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-slate-500">{summary.billing.outstandingInvoiceCount} invoice(s)</p>
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Trust Balance</p>
                        <p className={`text-xl font-bold ${summary.billing.trustBalanceCents >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                          ${(Math.abs(summary.billing.trustBalanceCents) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}
                          {summary.billing.trustBalanceCents < 0 && " (deficit)"}
                        </p>
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Preferred Payment Method</p>
                        <div className="mt-1">
                          <SearchableSelect
                            options={[
                              { value: "", label: "Firm Default" },
                              { value: "credit_card", label: "Credit Card" },
                              { value: "etransfer", label: "Interac e-Transfer" },
                              { value: "wire", label: "Wire Transfer" },
                              { value: "cheque", label: "Cheque" },
                              { value: "eft", label: "EFT (Direct Deposit)" },
                              { value: "cash", label: "Cash" },
                            ]}
                            value={client?.billing?.preferredPaymentMethod || ""}
                            onChange={async (val) => {
                              const value = val || null;
                              try {
                                await clientsApi.update(id!, { billing: { ...client?.billing, preferredPaymentMethod: value } });
                                setClient((prev: any) => ({
                                  ...prev,
                                  billing: { ...prev?.billing, preferredPaymentMethod: value },
                                }));
                              } catch (err) {
                                console.error("Failed to update payment method:", err);
                                notifyError("Couldn't update the preferred payment method.", err);
                              }
                            }}
                            placeholder="Firm Default"
                            clearable
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="animate-pulse space-y-3">
                      <div className="h-10 bg-slate-100 rounded-lg" />
                      <div className="h-6 bg-slate-100 rounded-lg w-1/2" />
                    </div>
                  )}
                </div>

                {/* Recent Messages Card */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                    Recent Messages
                  </h3>
                  {summary ? (
                    summary.conversations.length > 0 ? (
                      <div className="space-y-3">
                        {summary.conversations.map((conv: any) => (
                          <div key={conv._id} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-slate-500 uppercase">
                                {conv.channels?.primaryChannel || "message"}
                              </span>
                              <span className="text-xs text-slate-400">
                                {conv.lastMessageAt
                                  ? new Date(conv.lastMessageAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                  : ""}
                              </span>
                            </div>
                            <p className="text-sm text-slate-700 truncate">{conv.preview || conv.subject || "No preview"}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-4">
                        <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 mb-3">
                          <MessageSquare className="h-6 w-6" style={{ color: "var(--color-primary)" }} />
                        </div>
                        <p className="text-sm font-medium text-slate-600 mb-1">No conversations yet</p>
                        <p className="text-xs text-slate-400 text-center">Send a message to start the conversation</p>
                      </div>
                    )
                  ) : (
                    <div className="animate-pulse space-y-3">
                      <div className="h-16 bg-slate-100 rounded-lg" />
                      <div className="h-16 bg-slate-100 rounded-lg" />
                    </div>
                  )}
                </div>
              </div>

              {/* Existing cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Contact Info */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    {client.emails?.map((e: any, i: number) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <span className="text-slate-700">{e.email}</span>
                        <span className="text-xs text-slate-400">({e.label})</span>
                      </div>
                    ))}
                    {client.phones?.map((p: any, i: number) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <Phone className="h-4 w-4 text-slate-400" />
                        <span className="text-slate-700">{formatPhoneNumber(p.number)}</span>
                        <span className="text-xs text-slate-400">({p.label})</span>
                      </div>
                    ))}
                    {client.addresses?.map((a: any, i: number) => (
                      <div key={i} className="flex items-start gap-3 text-sm">
                        <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                        <span className="text-slate-700">
                          {[a.line1, a.line2, a.city, a.region, a.postal, a.country]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                    ))}
                    {!client.emails?.length && !client.phones?.length && !client.addresses?.length && (
                      <div className="flex flex-col items-center justify-center py-4">
                        <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 mb-3">
                          <Mail className="h-6 w-6" style={{ color: "var(--color-primary)" }} />
                        </div>
                        <p className="text-sm font-medium text-slate-600 mb-1">No contact info on file</p>
                        <p className="text-xs text-slate-400 text-center">Add an email or phone number to enable communications</p>
                        <button
                          onClick={() => setEditing(true)}
                          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold transition-colors"
                          style={{ color: "var(--color-primary)" }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-primary-hover)")}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-primary)")}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add Contact Info
                        </button>
                      </div>
                    )}
                  </div>
                </div>


                {/* Assigned Team */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Users className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                      Assigned Team
                    </h3>
                    <span className="text-xs text-slate-400">
                      {client.mattersCount || 0} matter{(client.mattersCount || 0) !== 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    {(client.assignedUserIds || []).length > 0 ? (
                      (client.assignedUserIds || []).map((user: any) => (
                        <div
                          key={user._id}
                          className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{ backgroundColor: "var(--color-accent)" }}
                            >
                              {(user.name || user.email || "?")
                                .split(" ")
                                .slice(0, 2)
                                .map((w: string) => w[0]?.toUpperCase())
                                .join("")}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-700 truncate">
                                {user.name || "Unnamed"}
                              </p>
                              <p className="text-xs text-slate-400 truncate">{user.email}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveTeamMember(user._id)}
                            className="p-1 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                            title="Remove team member"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center py-4">
                        <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 mb-3">
                          <Users className="h-6 w-6" style={{ color: "var(--color-primary)" }} />
                        </div>
                        <p className="text-sm font-medium text-slate-600 mb-1">No team members assigned</p>
                        <p className="text-xs text-slate-400 text-center">Assign team members to collaborate on this client</p>
                      </div>
                    )}
                  </div>

                  {addingTeamMember ? (
                    <div className="space-y-2">
                      <SearchableSelect
                        options={firmUsers
                          .filter(
                            (u: any) =>
                              !(client.assignedUserIds || []).some(
                                (au: any) => (au._id || au) === u._id
                              )
                          )
                          .map((u: any) => ({
                            value: u._id,
                            label: u.name || u.email,
                          }))}
                        value={null}
                        onChange={(val) => {
                          if (val) handleAddTeamMember(val);
                        }}
                        placeholder="Search team members..."
                      />
                      <button
                        onClick={() => setAddingTeamMember(false)}
                        className="text-xs text-slate-400 hover:text-slate-600"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddingTeamMember(true)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
                      style={{ color: "var(--color-primary)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-primary-hover)")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-primary)")}
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Add Team Member
                    </button>
                  )}
                </div>

              </div>

              {/* Communication Preferences Card */}
              <CommunicationPreferencesCard
                client={client}
                editing={editingCommPrefs}
                form={commPrefsForm}
                saving={savingCommPrefs}
                onStartEdit={() => {
                  const prefs = client.communicationPreferences || {};
                  const formVals: Record<string, string> = {};
                  const notifKeys = ["invoiceNotifications", "paymentReceipts", "appointmentReminders", "matterUpdates", "documentRequests", "preferredChannel"];
                  notifKeys.forEach((k) => {
                    if (prefs[k]) formVals[k] = prefs[k];
                  });
                  if (typeof prefs.generalCommunications === "boolean") {
                    formVals.generalCommunications = prefs.generalCommunications ? "yes" : "no";
                  }
                  setCommPrefsForm(formVals);
                  setEditingCommPrefs(true);
                }}
                onCancel={() => setEditingCommPrefs(false)}
                onChange={setCommPrefsForm}
                onSave={async () => {
                  if (!id) return;
                  setSavingCommPrefs(true);
                  try {
                    const communicationPreferences: Record<string, any> = {};
                    Object.entries(commPrefsForm).forEach(([key, val]) => {
                      if (val && val !== "") {
                        if (key === "generalCommunications") {
                          communicationPreferences[key] = val === "yes";
                        } else {
                          communicationPreferences[key] = val;
                        }
                      }
                    });
                    await clientsApi.update(id, { communicationPreferences });
                    setEditingCommPrefs(false);
                    fetchClient();
                  } catch (err) {
                    console.error("Failed to update communication preferences:", err);
                    notifyError("Couldn't save communication preferences.", err);
                  } finally {
                    setSavingCommPrefs(false);
                  }
                }}
              />
            </div>
          )}

          {activeTab === "matters" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {matters.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="flex flex-col items-center justify-center py-20"
                >
                  <div className="relative mb-6">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                    <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                      <Briefcase className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">No matters yet</h3>
                  <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
                    Create a matter to start tracking legal work, deadlines, and billing for {client.displayName}.
                  </p>
                  <button
                    onClick={() => navigate("/matters")}
                    className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
                    style={{ backgroundColor: "var(--color-primary)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
                  >
                    <Plus className="h-5 w-5" />
                    Create a Matter
                  </button>
                  <div className="mt-8 flex flex-wrap justify-center gap-4 max-w-lg">
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                      Track deadlines & tasks
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                      Log time & generate invoices
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                      Store documents securely
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">
                          Matter/File #
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">
                          Title
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">
                          Practice Area
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {matters.map((m: any) => (
                        <tr
                          key={m._id}
                          onClick={() => openMatterInspector(m._id)}
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <td className="px-6 py-4 text-sm font-mono text-slate-600">{m.matterNumber}</td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-800">{m.title}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {(m.practiceAreas || [m.practiceArea]).filter(Boolean).map((pa: string) => formatPracticeArea(pa)).join(", ")}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                statusBadge[m.status] || "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {m.status?.charAt(0).toUpperCase() + m.status?.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "documents" && id && (
            <ClientDocumentsSection clientId={id} />
          )}

          {activeTab === "billing" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="flex flex-col items-center justify-center py-20"
              >
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                  <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                    <DollarSign className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Client Billing</h3>
                <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
                  View invoices, payments, and complete billing history for {client.displayName}.
                  This feature is coming soon.
                </p>
                <div className="mt-2 flex flex-wrap justify-center gap-4 max-w-lg">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <ReceiptText className="h-3.5 w-3.5 text-emerald-400" />
                    Invoices & payments
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <CreditCard className="h-3.5 w-3.5 text-emerald-400" />
                    Online payment collection
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                    Trust accounting
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {activeTab === "messages" && id && (
            <ClientMessagesTab clientId={id} onCompose={() => setShowComposeModal(true)} />
          )}

          {activeTab === "portal" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="flex flex-col items-center justify-center py-20"
              >
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                  <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                    <Globe className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Client Portal</h3>
                <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
                  Give {client.displayName} secure access to view their matters, documents, and invoices
                  online. Coming soon.
                </p>
                <div className="mt-2 flex flex-wrap justify-center gap-4 max-w-lg">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                    Secure login for clients
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <FileText className="h-3.5 w-3.5 text-emerald-400" />
                    Share documents & invoices
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <ExternalLink className="h-3.5 w-3.5 text-emerald-400" />
                    Self-service access
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Edit Modal */}
      <PremiumModal
        open={editing}
        onClose={() => setEditing(false)}
        title="Edit Client"
        icon={Edit3}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setEditing(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              <Save className="h-4 w-4" />
              Save
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
            <input
              type="text"
              value={editForm.displayName}
              onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="tel"
              value={editForm.phone}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
        </div>
      </PremiumModal>

      {/* Compose Message Modal */}
      <ComposeMessageModal
        open={showComposeModal}
        onClose={() => setShowComposeModal(false)}
        clientName={client.displayName}
        clientPhone={client.phones?.[0]?.number}
        clientEmail={client.emails?.[0]?.email}
        clientId={id!}
      />

      {/* Delete Confirmation Modal */}
      <PremiumModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Client"
        icon={Trash2}
        size="sm"
        footer={
          isAdmin ? (
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? "Deleting..." : "Yes, Delete Client"}
              </button>
            </div>
          ) : (
            <div className="flex justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          )
        }
      >
        {isAdmin ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">This action cannot be undone</p>
                <p className="text-sm text-red-600 mt-1">
                  Deleting this client will remove them from all views. Matters and invoices associated with this client will remain but will no longer be linked.
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              Are you sure you want to delete <span className="font-semibold">{client?.displayName}</span>?
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Administrator access required</p>
              <p className="text-sm text-amber-600 mt-1">
                Only firm administrators can delete clients. Please contact your administrator to remove this client.
              </p>
            </div>
          </div>
        )}
      </PremiumModal>
    </div>
  );
};

export default ClientDetail;
